/**
 * Slide-deck view — the shared, read-only presentation surface.
 *
 * Layout: thumbnail sidebar on the left, single-slide stage on the right,
 * each slide rendered inside its own sandboxed iframe (CSS-scaled in the
 * thumbnails, uniformly scaled-to-fit on the stage) so the model's Tailwind
 * classes resolve without leaking styles into the host app.
 *
 * This is the single component both surfaces render through:
 *  - the chat artifact canvas ({@link PresentationCanvas}) drives it in
 *    *controlled* mode, threading its streaming/edit `activeId` in so the
 *    stage follows the slide being generated or edited;
 *  - the Library file-preview ({@link PresentationRenderer}) drives it
 *    *uncontrolled* and read-only, so opening a `.pptx`-style artifact from
 *    the Library looks exactly like it does in chat instead of a flat,
 *    PDF-like vertical scroll.
 *
 * It owns only the deck body (sidebar + stage). Chrome (title, download,
 * close, streaming skeleton) stays with each caller because it differs
 * between the canvas header and the dialog header.
 *
 * @module components/artifacts/slide-deck-view
 */

import { Loader2 } from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from "react";

import { ScrollArea } from "@/components/ui/scroll-area";
import { ARTIFACT_STYLE_PRELUDE_HTML } from "@/lib/artifacts/style-guide";
import { hashFrameContent } from "@/lib/artifacts/frame-key";
import type { ParsedSlide } from "@/lib/artifacts/slides";
import { cn } from "@/lib/utils";

/** A slide to render, tagged with whether it is currently being edited. */
export type DisplaySlide = ParsedSlide & { streaming: boolean };

/** 16:9 viewport the slide HTML is authored against. */
const SLIDE_WIDTH = 1280;
const SLIDE_HEIGHT = 720;
const SLIDE_ASPECT = SLIDE_WIDTH / SLIDE_HEIGHT;

/**
 * How many thumbnails may fill the sidebar before it scrolls. At or below
 * this count the thumbnails grow to use the full height (so a 5-slide deck
 * fills the column instead of clustering at the top); above it, they cap at
 * the "8 fit" size and the sidebar scrolls. See {@link SlideSidebar}.
 */
const MAX_VISIBLE_THUMBS = 8;
/** Vertical gap between thumbnails (matches `gap-2`). */
const THUMB_GAP = 8;
/** Sidebar padding (matches `p-3`). */
const SIDEBAR_PADDING = 12;
/**
 * Thumbnail sizing bounds. Width is the primary constraint so the rail stays
 * narrow and a consistent width regardless of slide count: thumbnails grow to
 * fill the height (few-slide decks get taller thumbs) only until they hit
 * MAX_THUMB_WIDTH, after which they stop widening. Height also has a floor so
 * many-slide decks stay legible before the rail scrolls.
 */
const MAX_THUMB_WIDTH = 150;
const MAX_THUMB_HEIGHT = MAX_THUMB_WIDTH / SLIDE_ASPECT;
const MIN_THUMB_HEIGHT = 66;
/** Wheel/touchpad delta needed before moving to the next slide. */
const WHEEL_NAVIGATION_THRESHOLD = 80;
/** Cooldown prevents one touchpad gesture from skipping multiple slides. */
const WHEEL_NAVIGATION_COOLDOWN_MS = 520;
/** Browser line-mode wheel deltas are converted to an approximate pixel value. */
const WHEEL_LINE_DELTA_PX = 32;
/** Mobile/desktop page padding, kept in sync with SlidePagerStage classes. */
const MOBILE_STAGE_PADDING = 32;
const DESKTOP_STAGE_PADDING = 64;

export interface SlideDeckViewProps {
  /** Slides to render, in order. */
  slides: DisplaySlide[];
  /** Deck title — used only for iframe `title` attributes (a11y). */
  title?: string;
  /**
   * Controlled active-slide id. When provided the caller owns selection
   * (the chat canvas needs this to follow streaming/edits). Omit it to let
   * the view manage its own selection — the read-only Library path.
   */
  activeId?: string;
  /** Selection callback. Fired on thumbnail click in both modes. */
  onSelectSlide?: (id: string) => void;
  /** True while a fresh deck is still streaming in — shows the "Generating…" tile. */
  isStreamingCreate?: boolean;
  /**
   * Render a "Slide X of Y" pill on the stage. The chat canvas shows this in
   * its header instead, so it opts out; the Library dialog's generic chrome
   * has no slide counter, so it opts in.
   */
  showSlideCounter?: boolean;
  className?: string;
}

/**
 * Shared slide-deck body. See the module doc for the controlled vs.
 * uncontrolled split.
 */
export function SlideDeckView({
  slides,
  title,
  activeId,
  onSelectSlide,
  isStreamingCreate = false,
  showSlideCounter = false,
  className,
}: SlideDeckViewProps) {
  const controlled = activeId !== undefined;
  const [internalId, setInternalId] = useState<string>("");
  const deckRef = useRef<HTMLDivElement | null>(null);

  /**
   * Uncontrolled selection housekeeping — snap to the first slide when the
   * current selection isn't in the deck (initial mount, deck swap). The
   * controlled path leaves this to the parent, which has richer rules
   * (follow the streaming slide, jump to the edited one).
   */
  useEffect(() => {
    if (controlled) return;
    if (slides.length === 0) {
      if (internalId !== "") setInternalId("");
      return;
    }
    if (!slides.some((s) => s.id === internalId)) {
      setInternalId(slides[0].id);
    }
  }, [controlled, slides, internalId]);

  const currentId = controlled ? activeId : internalId;
  const activeSlide =
    slides.find((s) => s.id === currentId) ?? slides[0] ?? null;
  const activeIndex = activeSlide
    ? slides.findIndex((s) => s.id === activeSlide.id)
    : -1;

  const handleSelect = useCallback((id: string) => {
    onSelectSlide?.(id);
    if (!controlled) setInternalId(id);
  }, [controlled, onSelectSlide]);

  const handleSelectIndex = useCallback((index: number) => {
    if (slides.length === 0) return;
    const clamped = Math.min(Math.max(index, 0), slides.length - 1);
    const next = slides[clamped];
    if (!next) return;
    handleSelect(next.id);
  }, [handleSelect, slides]);

  const handleDeckKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (slides.length <= 1 || isEditableKeyTarget(event.target)) return;

    const currentIndex = activeIndex >= 0 ? activeIndex : 0;
    if (
      event.key === "ArrowDown" ||
      event.key === "PageDown" ||
      (event.key === " " && !event.shiftKey)
    ) {
      event.preventDefault();
      handleSelectIndex(currentIndex + 1);
      return;
    }

    if (
      event.key === "ArrowUp" ||
      event.key === "PageUp" ||
      (event.key === " " && event.shiftKey)
    ) {
      event.preventDefault();
      handleSelectIndex(currentIndex - 1);
      return;
    }

    if (event.key === "Home") {
      event.preventDefault();
      handleSelectIndex(0);
      return;
    }

    if (event.key === "End") {
      event.preventDefault();
      handleSelectIndex(slides.length - 1);
    }
  }, [activeIndex, handleSelectIndex, slides.length]);

  const handleDeckMouseDown = useCallback((event: MouseEvent<HTMLDivElement>) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target.closest("button,a,input,textarea,select,[contenteditable='true']")) return;
    deckRef.current?.focus({ preventScroll: true });
  }, []);

  return (
    <div
      ref={deckRef}
      role="region"
      aria-label={title ? `${title} slide deck` : "Slide deck"}
      tabIndex={0}
      onKeyDown={handleDeckKeyDown}
      onMouseDown={handleDeckMouseDown}
      className={cn(
        "flex flex-1 overflow-hidden bg-muted/20 outline-none focus-visible:ring-2 focus-visible:ring-ring",
        className,
      )}
    >
      <SlideSidebar
        slides={slides}
        activeId={activeSlide?.id ?? ""}
        onSelect={handleSelect}
        isStreamingCreate={isStreamingCreate}
      />
      <SlidePagerStage
        slides={slides}
        activeId={activeSlide?.id ?? ""}
        activeIndex={activeIndex}
        title={title}
        isStreamingCreate={isStreamingCreate}
        hasSlides={slides.length > 0}
        onSelectIndex={handleSelectIndex}
        counterLabel={
          showSlideCounter && slides.length > 0 && activeIndex >= 0
            ? `Slide ${activeIndex + 1} of ${slides.length}`
            : null
        }
      />
    </div>
  );
}

/**
 * Vertical list of CSS-scaled slide thumbnails. Each thumbnail is a tiny
 * sandboxed iframe rendering one `<section>` so the user sees an accurate
 * preview without us having to rasterize anything server-side.
 */
function SlideSidebar({
  slides,
  activeId,
  onSelect,
  isStreamingCreate,
}: {
  slides: DisplaySlide[];
  activeId: string;
  onSelect: (id: string) => void;
  isStreamingCreate: boolean;
}) {
  /**
   * Measure the sidebar height so we can size thumbnails to fill it. Up to
   * MAX_VISIBLE_THUMBS thumbnails share the available height evenly (a
   * 5-slide deck fills the column); beyond that the size caps at the "8 fit"
   * height and the extra thumbnails overflow into the scroll region. The
   * `+1` for the streaming "Generating…" tile keeps sizing stable as the
   * final slide streams in. useLayoutEffect measures before paint.
   */
  const asideRef = useRef<HTMLElement | null>(null);
  const [height, setHeight] = useState(0);
  useLayoutEffect(() => {
    const el = asideRef.current;
    if (!el) return;
    const measure = () => setHeight(el.clientHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const tileCount = Math.max(slides.length, 1) + (isStreamingCreate ? 1 : 0);
  const visible = Math.min(Math.max(tileCount, 1), MAX_VISIBLE_THUMBS);
  const contentHeight = height - SIDEBAR_PADDING * 2 - THUMB_GAP * (visible - 1);
  const rawHeight = contentHeight > 0 ? contentHeight / visible : MIN_THUMB_HEIGHT;
  const thumbHeight = Math.round(
    Math.min(MAX_THUMB_HEIGHT, Math.max(MIN_THUMB_HEIGHT, rawHeight)),
  );
  const thumbWidth = Math.round(thumbHeight * SLIDE_ASPECT);

  return (
    <aside
      ref={asideRef}
      className="shrink-0 border-r bg-muted/30"
      style={{ width: thumbWidth + SIDEBAR_PADDING * 2 }}
    >
      {/*
       * shadcn ScrollArea (Radix) owns the vertical scroll: its scrollbar is
       * an overlay styled with the design tokens (thin, muted thumb) that
       * sits in the right padding, so we don't manage overflow or scrollbar
       * colors by hand. `p-3` here matches SIDEBAR_PADDING in the sizing math.
       */}
      <ScrollArea className="h-full">
        <div className="flex flex-col items-center gap-2 p-3">
          {slides.length === 0 ? (
            <div className="px-2 py-6 text-center text-xs text-muted-foreground">
              No slides yet.
            </div>
          ) : (
            slides.map((slide, idx) => (
              <SlideThumb
                key={slide.id}
                slide={slide}
                index={idx + 1}
                active={slide.id === activeId}
                streaming={slide.streaming}
                width={thumbWidth}
                height={thumbHeight}
                onClick={() => onSelect(slide.id)}
              />
            ))
          )}
          {isStreamingCreate ? (
            <div
              className="flex shrink-0 items-center justify-center gap-2 rounded border-2 border-dashed border-muted-foreground/20 bg-muted/40 text-[11px] font-medium text-muted-foreground"
              style={{ width: thumbWidth, height: thumbHeight }}
            >
              <Loader2 className="size-3 animate-spin" />
              <span>Generating…</span>
            </div>
          ) : null}
        </div>
      </ScrollArea>
    </aside>
  );
}

/**
 * One thumbnail. Wraps the slide HTML in a minimal Tailwind-loaded shell so
 * the model's utility classes resolve, then scales the whole iframe down.
 * `pointer-events-none` on the iframe lets the surrounding button receive
 * the click instead of the iframe content.
 */
function SlideThumb({
  slide,
  index,
  active,
  streaming,
  width,
  height,
  onClick,
}: {
  slide: DisplaySlide;
  index: number;
  active: boolean;
  streaming: boolean;
  width: number;
  height: number;
  onClick: () => void;
}) {
  /**
   * Keep the active thumbnail in view when selection changes — needed once
   * the sidebar scrolls (>MAX_VISIBLE_THUMBS slides), e.g. when a create
   * stream follows the latest slide or the user pages past the fold.
   */
  const ref = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (active) ref.current?.scrollIntoView({ block: "nearest" });
  }, [active]);

  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      className={cn(
        "group relative block shrink-0 overflow-hidden rounded border bg-white shadow-sm transition",
        streaming
          ? "ring-2 ring-primary"
          : active
            ? "ring-2 ring-primary"
            : "hover:ring-1 hover:ring-muted-foreground/40",
      )}
      style={{ width, height }}
    >
      <iframe
        key={slideFrameKey(slide.id, slide.html)}
        title={`Slide ${index} thumbnail`}
        sandbox="allow-scripts"
        srcDoc={wrapSlideForIframe(slide.html)}
        className="pointer-events-none absolute left-0 top-0 origin-top-left border-0"
        style={{
          width: SLIDE_WIDTH,
          height: SLIDE_HEIGHT,
          transform: `scale(${width / SLIDE_WIDTH})`,
        }}
      />
      {streaming ? (
        <span className="absolute left-1 top-1 flex items-center gap-1 rounded bg-primary/90 px-1.5 py-0.5 text-[9px] font-medium text-primary-foreground shadow-sm">
          <Loader2 className="size-2.5 animate-spin" />
          Editing
        </span>
      ) : null}
      <span className="absolute bottom-1 right-2 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
        {index}
      </span>
    </button>
  );
}

/**
 * Full-size active slide rendered inside its own sandboxed iframe. Uses
 * uniform scaling on the wrapper so the slide stays at its authored 16:9
 * size and adapts to the available pane width.
 */
function SlidePagerStage({
  slides,
  activeId,
  activeIndex,
  title,
  isStreamingCreate,
  hasSlides,
  onSelectIndex,
  counterLabel,
}: {
  slides: DisplaySlide[];
  activeId: string;
  activeIndex: number;
  title?: string;
  isStreamingCreate: boolean;
  hasSlides: boolean;
  onSelectIndex: (index: number) => void;
  counterLabel: string | null;
}) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const slideRefs = useRef(new Map<string, HTMLDivElement>());
  const activeIndexRef = useRef(activeIndex);
  const onSelectIndexRef = useRef(onSelectIndex);
  const wheelAccumulatedRef = useRef(0);
  const wheelCooldownUntilRef = useRef(0);
  const scrollTimerRef = useRef<number | null>(null);
  const [scale, setScale] = useState(0);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    onSelectIndexRef.current = onSelectIndex;
  }, [onSelectIndex]);

  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const recompute = () => {
      const padding = el.clientWidth < 768 ? MOBILE_STAGE_PADDING : DESKTOP_STAGE_PADDING;
      const availableWidth = el.clientWidth - padding;
      const availableHeight = el.clientHeight - padding;
      if (availableWidth > 0 && availableHeight > 0) {
        setScale(Math.min(availableWidth / SLIDE_WIDTH, availableHeight / SLIDE_HEIGHT));
      }
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (!activeId) return;
    const scroller = stageRef.current;
    const target = slideRefs.current.get(activeId);
    if (!scroller || !target) return;
    const top = target.offsetTop;
    if (Math.abs(scroller.scrollTop - top) < 2) return;
    scroller.scrollTo({ top, behavior: "smooth" });
  }, [activeId, slides.length]);

  useEffect(() => {
    const scroller = stageRef.current;
    if (!scroller) return;

    function handleScroll() {
      if (scrollTimerRef.current !== null) window.clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = window.setTimeout(() => {
        const currentScroller = stageRef.current;
        if (!currentScroller || slides.length === 0) return;
        const nextIndex = Math.round(currentScroller.scrollTop / currentScroller.clientHeight);
        if (nextIndex !== activeIndexRef.current) {
          onSelectIndexRef.current(nextIndex);
        }
      }, 120);
    }

    scroller.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      scroller.removeEventListener("scroll", handleScroll);
      if (scrollTimerRef.current !== null) window.clearTimeout(scrollTimerRef.current);
    };
  }, [slides.length]);

  useEffect(() => {
    const scroller = stageRef.current;
    if (!scroller) return;
    const wheelRoot = scroller;

    function handleWheel(event: WheelEvent) {
      if (slides.length <= 1) return;
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;

      event.preventDefault();
      const now = performance.now();
      if (now < wheelCooldownUntilRef.current) return;

      wheelAccumulatedRef.current += normalizeWheelDeltaY(event, wheelRoot.clientHeight);
      if (Math.abs(wheelAccumulatedRef.current) < WHEEL_NAVIGATION_THRESHOLD) return;

      const direction = wheelAccumulatedRef.current > 0 ? 1 : -1;
      wheelAccumulatedRef.current = 0;
      wheelCooldownUntilRef.current = now + WHEEL_NAVIGATION_COOLDOWN_MS;
      onSelectIndexRef.current(activeIndexRef.current + direction);
    }

    wheelRoot.addEventListener("wheel", handleWheel, { passive: false });
    return () => wheelRoot.removeEventListener("wheel", handleWheel);
  }, [slides.length]);

  const setSlideRef = useCallback((id: string, node: HTMLDivElement | null) => {
    if (node) slideRefs.current.set(id, node);
    else slideRefs.current.delete(id);
  }, []);

  return (
    <div className="relative flex flex-1 overflow-hidden bg-muted/40">
      <div
        ref={stageRef}
        data-testid="slide-pager-stage"
        className="h-full flex-1 snap-y snap-mandatory overflow-y-auto overflow-x-hidden overscroll-contain scroll-smooth scrollbar-hide [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {slides.length === 0 ? (
          <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
            {isStreamingCreate && !hasSlides ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                <span>Generating the first slide...</span>
              </>
            ) : (
              <span>No slide selected.</span>
            )}
          </div>
        ) : (
          slides.map((slide, index) => (
            <div
              key={slide.id}
              ref={(node) => setSlideRef(slide.id, node)}
              data-testid={"slide-pager-page-" + (index + 1)}
              className="flex h-full min-h-full snap-center items-center justify-center p-4 md:p-8"
              style={{ scrollSnapStop: "always" }}
              aria-hidden={slide.id !== activeId}
            >
              <div
                className="relative overflow-hidden rounded-lg bg-white shadow-xl"
                style={{ width: SLIDE_WIDTH * scale, height: SLIDE_HEIGHT * scale }}
              >
                <iframe
                  key={slideFrameKey(slide.id, slide.html)}
                  title={(title || "Slide") + " " + (index + 1)}
                  sandbox="allow-scripts"
                  srcDoc={wrapSlideForIframe(slide.html)}
                  tabIndex={-1}
                  className="pointer-events-none absolute left-0 top-0 origin-top-left border-0"
                  style={{
                    width: SLIDE_WIDTH,
                    height: SLIDE_HEIGHT,
                    transform: "scale(" + scale + ")",
                  }}
                />
                {slide.streaming ? (
                  <div className="pointer-events-none absolute right-3 top-3 flex items-center gap-1.5 rounded-full border bg-background/90 px-2.5 py-1 text-[11px] font-medium text-muted-foreground shadow-sm backdrop-blur">
                    <Loader2 className="size-3 animate-spin text-primary" />
                    Updating this slide...
                  </div>
                ) : null}
              </div>
            </div>
          ))
        )}
      </div>
      {counterLabel ? (
        <span className="pointer-events-none absolute bottom-3 right-4 rounded-full border bg-background/90 px-2.5 py-1 text-[11px] font-medium text-muted-foreground shadow-sm backdrop-blur">
          {counterLabel}
        </span>
      ) : null}
    </div>
  );
}

function isEditableKeyTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return Boolean(target.closest("input,textarea,select,[contenteditable='true']"));
}

function normalizeWheelDeltaY(event: WheelEvent, pageHeight: number): number {
  if (event.deltaMode === 1) return event.deltaY * WHEEL_LINE_DELTA_PX;
  if (event.deltaMode === 2) return event.deltaY * pageHeight;
  return event.deltaY;
}

/**
 * Wraps a single slide's HTML in a Tailwind-loaded document so the iframe
 * resolves utility classes the model emitted. Body sized to the authored
 * slide canvas so transforms in thumbnails scale predictably.
 */
/**
 * React `key` for a slide's iframe: its id plus a hash of its body.
 *
 * The id alone is stable across a slide's placeholder → finished transition,
 * so React reused one element and mutated `srcdoc` in place — the update
 * Chromium may not re-navigate. See {@link hashFrameContent} for the full
 * failure it caused.
 */
function slideFrameKey(id: string, html: string): string {
  return `${id}:${hashFrameContent(html)}`;
}

function wrapSlideForIframe(sectionHtml: string): string {
  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    ${ARTIFACT_STYLE_PRELUDE_HTML}
    <script src="https://cdn.tailwindcss.com"></script>
    <style>
      html, body { margin: 0; padding: 0; width: ${SLIDE_WIDTH}px; height: ${SLIDE_HEIGHT}px; overflow: hidden; }
      /*
       * !important on width/height AND max-width/min-width/max-height/
       * min-height: a model-authored sizing utility on the section (e.g.
       * max-w-6xl, which sets max-width — a distinct property from width,
       * so overriding width alone leaves max-width still constraining the
       * rendered box) would otherwise shrink that one slide below the
       * deck's fixed 1280x720 canvas — visibly mismatched against every
       * other slide in the thumbnail rail. The host's canvas size is not
       * negotiable, so it's enforced here rather than left to prompt
       * compliance alone.
       */
      body > section {
        width: ${SLIDE_WIDTH}px !important;
        height: ${SLIDE_HEIGHT}px !important;
        max-width: none !important;
        min-width: 0 !important;
        max-height: none !important;
        min-height: 0 !important;
        box-sizing: border-box;
      }
    </style>
  </head>
  <body>${sectionHtml}</body>
</html>`;
}
